import { Select } from '@traceair/tds';
import { UploadType } from '../../../../../consts/uploadType';
import { uploadTypeToHuman } from '../../helpers/uploadType';
import { stylesheet } from 'typestyle';
import { UploadTypeState } from './useUploadType';
import FlightDatePicker from './FlightDatePicker';
import { ScheduledFlight } from '../../../../../types';
import { useEffect, useMemo } from 'react';
import { GeorefData, RegistrationRequest, UploadData, UploadStoreState } from '../../../../../store/uploadStore';
import { addDays } from 'date-fns';
import { ProjectData } from '../../types';
import { dateToScheduleFormat } from '../../helpers/date';
import { makeSafeIsoDateString } from '../../../../../libs/doneDate';

const MAX_DATE_OFFSET_DAYS = 7;

const css = stylesheet({
  container: {
    display: 'flex',
    gap: 12,
  },
  fieldWrap: {
    width: 220,
    margin: '12px 0 0',
  },
});

interface Props extends ProjectData {
  isPano: boolean;
  schedule?: ScheduledFlight;
  showTypeSelect: boolean;
  projectDate?: string;
  setProjectDate: UploadStoreState['setProjectDate'];
  registrationRequests: Map<Promise<unknown>, RegistrationRequest> | undefined;
  upload: UploadData | undefined;
  georef: GeorefData | undefined;
  uploadType: UploadType;
  setUploadType: UploadTypeState['setUploadType'];
}

export const FlightUploadFields = ({
  isPano,
  customer,
  site,
  schedule,
  showTypeSelect,
  projectDate,
  setProjectDate,
  registrationRequests,
  upload,
  georef,
  uploadType,
  setUploadType,
}: Props) => {
  const dateStr = upload?.overview.date ?? schedule?.flightDateIso ?? projectDate;
  const date = useMemo(() => {
    return dateStr ? new Date(makeSafeIsoDateString(dateStr)) : undefined;
  }, [dateStr]);

  const hasRegistrationRequests = !!registrationRequests?.size;

  const disableTypeSelect = !!upload || !!georef || hasRegistrationRequests;
  const disableDatePicker = !!upload || !!schedule || hasRegistrationRequests;

  useEffect(() => {
    if (!schedule && !projectDate) {
      const today = dateToScheduleFormat(new Date());
      setProjectDate(isPano, customer.id, site.id, undefined, today);
    }
  }, []);

  const handleScanTypeChange = (s: UploadType) => setUploadType(s);
  const scanTypeOptions = [UploadType.RegularAndRtk, UploadType.Ppk, UploadType.Lidar].map((t) => ({
    label: uploadTypeToHuman(t),
    value: t,
  }));

  const datePicker = useMemo(() => {
    // The date picker component resets "open" state on each rerender, so we have to memoize it
    const maxScanDate = addDays(new Date(), MAX_DATE_OFFSET_DAYS);

    const handleDateChange = (d?: Date) => {
      if (d) {
        const date = dateToScheduleFormat(d);
        setProjectDate(isPano, customer.id, site.id, undefined, date);
      }
    };

    return (
      <FlightDatePicker
        isDisabled={disableDatePicker}
        date={date}
        maxDate={maxScanDate}
        onChange={handleDateChange}
        placeholder="Date"
      />
    );
  }, [disableDatePicker, date, setProjectDate]);

  if (!showTypeSelect && schedule) {
    return null;
  }

  return (
    <div className={css.container}>
      {showTypeSelect && (
        <div className={css.fieldWrap}>
          <Select
            disabled={disableTypeSelect}
            value={uploadType}
            label="Scan type"
            onChange={handleScanTypeChange}
            options={scanTypeOptions}
          />
        </div>
      )}

      {!schedule && <div className={css.fieldWrap}>{datePicker}</div>}
    </div>
  );
};
